/**
 * Centralized profile avatar resolution.
 *
 * Resolution priority:
 *  1. profile.profile_picture            (uploaded custom photo)
 *  2. profile.avatar_url                 (legacy/alternate column)
 *  3. authUser?.user_metadata.avatar_url (e.g. Google)
 *  4. authUser?.user_metadata.picture    (Google OAuth)
 *  5. DEFAULT_WHALE_AVATAR               (app default — every user gets the whale)
 *
 * Initials are ONLY rendered as a true last-resort fallback when even the
 * whale image fails to load. Callers should still pass `getAvatarInitial` to
 * AvatarFallback for that case.
 */

import type { User } from '@supabase/supabase-js';
import { useAuth } from '@/hooks/useAuth';
import { useCurrentUserProfile } from '@/hooks/useCurrentUserProfile';

export const DEFAULT_WHALE_AVATAR = '/default-whale-avatar.png';

export type AvatarProfileLike = {
  profile_picture?: string | null;
  avatar_url?: string | null;
  name?: string | null;
  username?: string | null;
  email?: string | null;
} | null | undefined;

const isNonEmpty = (v: unknown): v is string =>
  typeof v === 'string' && v.trim().length > 0 && v !== '/placeholder.svg';

/**
 * Returns the best avatar URL for a profile. Falls back to the app default
 * whale avatar so every surface renders consistently. Pass a `tag` to scope
 * the debug log (e.g. 'review-author', 'feed-card', 'comment-composer').
 */
export function resolveAvatarUrl(
  profile?: AvatarProfileLike,
  authUser?: User | null,
  tag: string = 'generic',
): string {
  const log = (source: string, value?: string) => {
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.debug(
        `[AvatarDebug][${tag}] source=${source}`,
        profile?.username || authUser?.email || '',
        value || '',
      );
    }
  };

  if (isNonEmpty(profile?.profile_picture)) {
    log('profile_picture');
    return profile!.profile_picture as string;
  }
  if (isNonEmpty(profile?.avatar_url)) {
    log('profile.avatar_url');
    return profile!.avatar_url as string;
  }
  const meta = (authUser?.user_metadata ?? {}) as Record<string, unknown>;
  if (isNonEmpty(meta.avatar_url)) {
    log('user_metadata.avatar_url');
    return meta.avatar_url as string;
  }
  if (isNonEmpty(meta.picture)) {
    log('user_metadata.picture');
    return meta.picture as string;
  }
  log('default_whale');
  return DEFAULT_WHALE_AVATAR;
}

/** First letter for the AvatarFallback (only shown if the image fails to load). */
export function getAvatarInitial(
  profile?: AvatarProfileLike,
  authUser?: User | null,
): string {
  const source =
    profile?.name ||
    profile?.username ||
    (authUser?.user_metadata as any)?.name ||
    (authUser?.user_metadata as any)?.full_name ||
    profile?.email ||
    authUser?.email ||
    'U';
  return source.trim().charAt(0).toUpperCase() || 'U';
}

/**
 * Hook variant: resolves the avatar for the currently signed-in user using the
 * freshest profile data + auth metadata. Always returns a usable `src` (whale
 * default if no custom photo).
 */
export function useResolvedAvatar(tag: string = 'current-user'): {
  src: string;
  initial: string;
} {
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile();
  return {
    src: resolveAvatarUrl(profile as AvatarProfileLike, user, tag),
    initial: getAvatarInitial(profile as AvatarProfileLike, user),
  };
}
